import { useMemo, useState } from "react";
import { AlertTriangleIcon, Loader2Icon, PlusIcon } from "lucide-react";
import type { Address } from "viem";
import { toast } from "sonner";
import { Button, Badge } from "../ui/button";
import { Card, CardContent, Desktop, MobileItem, MobileList, PageHeader } from "../ui/card";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/overlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox, Input, Label, Switch } from "../ui/form";
import { cn } from "../lib/cn";
import { round2, sum, timeText } from "../lib/format";
import { ServiceIcon } from "../lib/icons";
import { useN, useRich, useT, useTT, Y } from "../i18n";
import { useStore } from "../store/store";
import { useWallet } from "../store/wallet";
import { errorText, type ConversationView } from "../adapters/clients";
import { useEnsurePayerCap } from "../components/WalletBlock";
import type { ServiceEntry } from "../adapters/chain";
import { SignedOut, StatusDot, Waiting, Rejected, useBuy } from "../components/common";
import { navigate } from "../components/Shell";
import { capsText, useSignLines } from "../components/signing";

export function spentOf(c: ConversationView): Array<[string, number]> {
  const m = new Map<string, number>();
  for (const s of c.spends) {
    m.set(s.token, round2((m.get(s.token) ?? 0) + s.actual - (s.minted ?? 0)));
    if (s.credits && s.credits !== "0") m.set("wpCredit", round2((m.get("wpCredit") ?? 0) + Number(s.credits) / 1e6));
  }
  return [...m].filter(([, n]) => n > 0);
}

function DeployBadge({ c }: { c: ConversationView }) {
  const t = useT();
  return c.kind === "deploy" ? (
    <Badge variant="outline" className="ml-2 h-4 px-1.5 text-[10px] font-normal">
      {t("common.deployBadge")}
    </Badge>
  ) : null;
}

export function ConversationsPage() {
  const { conversations, signedIn, wpPerToken } = useStore();
  const t = useT();
  const tt = useTT();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  const spent = (c: ConversationView) => {
    const parts = spentOf(c);
    if (parts.length === 0) return <span className="font-mono">—</span>;
    return (
      <>
        <span className="font-mono">{parts.map(([tok, n]) => `${n} ${tok}`).join(" · ")}</span>{" "}
        <span className="text-muted-foreground">
          {t("common.wp", { n: sum(...parts.map(([tok, n]) => n * wpPerToken(tok))) })}
        </span>
      </>
    );
  };
  if (!signedIn) {
    return (
      <>
        <PageHeader
          title={t("conversations.title")}
          description={t("conversations.description")}
          action={
            <Button disabled>
              <PlusIcon />
              {t("conversations.new")}
            </Button>
          }
        />
        <SignedOut what="signedOut.conversations" />
      </>
    );
  }
  const model = (c: ConversationView) => c.model.replace("|", " · ");
  return (
    <>
      <PageHeader
        title={t("conversations.title")}
        description={t("conversations.description")}
        action={
          <Button
            onClick={() => {
              setKey((k) => k + 1);
              setOpen(true);
            }}
          >
            <PlusIcon />
            {t("conversations.new")}
          </Button>
        }
      />
      <Card className="py-0">
        <CardContent className="px-0">
          <MobileList className="px-4 py-4">
            {conversations.map((c) => (
              <MobileItem key={c.id}>
                <a href={`#/conversations/${c.id}`} className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 font-medium">
                    {c.title || t("conv.untitled")}
                    <DeployBadge c={c} />
                  </span>
                  <StatusDot status={c.status} />
                </a>
                <span className="font-mono text-xs text-muted-foreground">{model(c)}</span>
                <span className="flex justify-between gap-3 text-xs">
                  <span>{spent(c)}</span>
                  <span className="text-muted-foreground">{tt(timeText(c.lastActivity))}</span>
                </span>
              </MobileItem>
            ))}
            {conversations.length === 0 && (
              <li className="py-6 text-center text-sm text-muted-foreground">{t("conv.empty.new")}</li>
            )}
          </MobileList>
          <Desktop>
            <Table>
              <THead>
                <TR>
                  <TH className="pl-4">{t("col.title")}</TH>
                  <TH>{t("col.model")}</TH>
                  <TH>{t("col.status")}</TH>
                  <TH>{t("col.spent")}</TH>
                  <TH className="pr-4">{t("col.lastActivity")}</TH>
                </TR>
              </THead>
              <TBody>
                {conversations.map((c) => (
                  <TR key={c.id} className="cursor-pointer" onClick={() => navigate(`/conversations/${c.id}`)}>
                    <TD className="pl-4 font-medium">
                      <a
                        href={`#/conversations/${c.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                      >
                        {c.title || t("conv.untitled")}
                      </a>
                      <DeployBadge c={c} />
                    </TD>
                    <TD className="font-mono text-xs">{model(c)}</TD>
                    <TD>
                      <StatusDot status={c.status} />
                    </TD>
                    <TD className="text-xs">{spent(c)}</TD>
                    <TD className="pr-4 text-muted-foreground">{tt(timeText(c.lastActivity))}</TD>
                  </TR>
                ))}
                {conversations.length === 0 && (
                  <TR>
                    <TD colSpan={5} className="py-8 text-center text-muted-foreground">
                      {t("conv.empty.new")}
                    </TD>
                  </TR>
                )}
              </TBody>
            </Table>
          </Desktop>
        </CardContent>
      </Card>
      <NewConversationDialog key={key} open={open} onOpenChange={setOpen} />
    </>
  );
}

interface ModelChoice {
  id: string;
  gateway: string;
  model: string;
  token: string;
  display: string;
  entry: ServiceEntry;
}

export function modelsOf(entries: ServiceEntry[]): ModelChoice[] {
  return entries
    .filter((e) => e.kinds.includes("model"))
    .flatMap((e) =>
      (e.models ?? []).map((m) => ({
        id: `${e.name}|${m}`,
        gateway: e.name,
        model: m,
        token: e.token,
        display: `${e.name} · ${m}`,
        entry: e,
      })),
    );
}

/** The New conversation dialog (D63, D64, D67): services from My services, a cap per token, the balance check, Pay in wpCredit. */
export function NewConversationDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const {
    myServices,
    directory,
    balances,
    settings,
    approvedTokens,
    runner,
    refreshAuth,
    refreshConversations,
    wpPerToken,
    saveSettings,
  } = useStore();
  const { request } = useWallet();
  const { buying, onBuy } = useBuy();
  const lines = useSignLines();
  const ensurePayerCap = useEnsurePayerCap();
  const t = useT();
  const rich = useRich();
  const tt = useTT();
  const runners = useMemo(() => myServices.filter((e) => e.kinds.includes("runner")), [myServices]);
  const memories = useMemo(() => myServices.filter((e) => e.kinds.includes("memory")), [myServices]);
  const models = useMemo(() => modelsOf(myServices), [myServices]);
  const tools = myServices.filter((e) => e.kinds.includes("tool") && e.tool);
  const [runnerName, setRunnerName] = useState(() =>
    runners.some((e) => e.name === settings.defaultRunner) ? settings.defaultRunner : (runners[0]?.name ?? ""),
  );
  const [memoryName, setMemoryName] = useState(() =>
    memories.some((e) => e.name === settings.defaultMemory) ? settings.defaultMemory : (memories[0]?.name ?? ""),
  );
  const [modelId, setModelId] = useState(() =>
    models.some((m) => m.id === settings.defaultModel) ? settings.defaultModel : (models[0]?.id ?? ""),
  );
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [caps, setCaps] = useState<Record<string, number>>({});
  const [payInWp, setPayInWp] = useState(false);
  const [budget, setBudget] = useState<number | null>(null);
  const [state, setState] = useState<"idle" | "waiting" | "rejected">("idle");
  const waiting = state === "waiting";
  const runnerEntry = runners.find((e) => e.name === runnerName);
  const memoryEntry = memories.find((e) => e.name === memoryName);
  const model = models.find((m) => m.id === modelId);
  const chosenTools = tools.filter((e) => selectedTools.includes(e.name));
  const tokensNeeded = [
    ...new Set([
      ...(runnerEntry ? [runnerEntry.token] : []),
      ...(memoryEntry ? [memoryEntry.token] : []),
      ...(model ? [model.token] : []),
      ...chosenTools.map((e) => e.token),
    ]),
  ];
  const defaultCapOf = (token: string) =>
    settings.defaultCaps[token] ??
    directory.find((e) => e.token === token && e.defaultCap !== undefined)?.defaultCap ??
    5;
  const capOf = (token: string) => caps[token] ?? defaultCapOf(token);
  const balanceOf = (token: string) => balances[token] ?? 0;
  const totalWp = sum(...tokensNeeded.map((tok) => capOf(tok) * wpPerToken(tok)));
  const budgetValue = budget ?? totalWp;
  const allCaps: Record<string, number> = {
    ...Object.fromEntries(tokensNeeded.map((tok) => [tok, capOf(tok)])),
    ...(payInWp ? { wpCredit: budgetValue } : {}),
  };
  const approveNeeded = Object.keys(allCaps).filter((tok) => !approvedTokens.has(tok));
  const above = (token: string) => !payInWp && capOf(token) > balanceOf(token);
  const owners = new Map<string, string>();
  if (runnerEntry) owners.set(runnerEntry.token, t("kind.runner"));
  if (memoryEntry && !owners.has(memoryEntry.token)) owners.set(memoryEntry.token, t("kind.memory"));
  if (model && !owners.has(model.token)) owners.set(model.token, t("kind.model"));
  for (const e of chosenTools) if (!owners.has(e.token)) owners.set(e.token, e.tool ?? e.name);
  const valid =
    !!runnerEntry &&
    !!memoryEntry &&
    !!model &&
    tokensNeeded.every((tok) => capOf(tok) > 0 && !above(tok)) &&
    (!payInWp || budgetValue > 0);

  function capCell(id: string, token: string, className?: string) {
    const bal = balanceOf(token);
    return (
      <div className={cn("grid gap-1 sm:w-52", className)}>
        <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">
          {rich("cell.cap", { token: <span className="font-mono">{token}</span> })}
        </Label>
        <Input
          id={id}
          type="number"
          min={1}
          step={1}
          className="w-24"
          value={capOf(token)}
          onChange={(e) => setCaps((c) => ({ ...c, [token]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
        />
        <div className="text-[11px] leading-snug text-muted-foreground">
          <div>{t("cell.hold", { n: bal, token })}</div>
          <div className="font-mono">{t("rate.line", { n: round2(1 / Math.max(wpPerToken(token), 1e-9)), token })}</div>
          <div>{t("common.wp", { n: round2(capOf(token) * wpPerToken(token)) })}</div>
          {above(token) && (
            <div className="text-amber-700 dark:text-amber-400">
              {rich("cell.above", {
                n: String(bal),
                cap: String(capOf(token)),
                buy: (
                  <button type="button" className="font-medium underline" onClick={() => onBuy(token)}>
                    {t("cell.buy", { token })}
                  </button>
                ),
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  async function create() {
    if (!runnerEntry || !memoryEntry || !model || !valid || !runner) return;
    setState("waiting");
    let created: { conversationId: string; agent: Address };
    try {
      // the runner becomes the default; the memory is this conversation's own choice (the default memory moves the
      // settings record and needs sign-ins: the Settings page does that, issue #2)
      if (settings.defaultRunner !== runnerEntry.name) {
        // the record's write is a commit paid by the Console's identity (D75): its cap first
        if (!(await ensurePayerCap([settings.defaultMemory]))) {
          setState("idle");
          return;
        }
        await saveSettings({ defaultRunner: runnerEntry.name });
      }
      created = await runner.create({
        memory: memoryEntry.name,
        model: model.id,
        tools: chosenTools.map((e) => e.name),
        effort: settings.defaultEffort,
        caps: Object.fromEntries(tokensNeeded.map((tok) => [tok, capOf(tok)])),
        ...(payInWp ? { budget: String(Math.round(budgetValue * 1e6)) } : {}),
      });
    } catch (e) {
      setState("idle");
      toast.error(t("conv.sendFailed", { detail: errorText(e) }));
      return;
    }
    const identity = created.agent;
    request({
      title: Y("wallet.req.allowNew"),
      amount: totalWp + (payInWp ? budgetValue : 0),
      tokens: Object.entries(allCaps).map(([symbol, amount]) => ({ symbol, amount })),
      lines: [
        ...approveNeeded.map((tok) => lines.approve(tok)),
        lines.authorize(Y("wallet.plain.authorizeNew", { caps: capsText(allCaps) }), identity, allCaps),
      ],
      onDone: async (r) => {
        if (r !== "signed") {
          setState("rejected");
          await refreshConversations();
          return;
        }
        await refreshAuth(created.conversationId, identity);
        await refreshConversations();
        onOpenChange(false);
        navigate(`/conversations/${created.conversationId}`);
      },
    });
  }

  const locked = waiting || buying !== null;
  return (
    <Dialog open={open} onOpenChange={(v) => !locked && onOpenChange(v)}>
      <DialogContent
        className="max-h-[90svh] overflow-y-auto sm:max-w-xl"
        onInteractOutside={(e) => locked && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("dialog.title")}</DialogTitle>
          <DialogDescription>
            {runnerEntry && memoryEntry
              ? rich("dialog.description", {
                  runner: <span className="font-mono">{runnerEntry.name}</span>,
                  identity: <span className="whitespace-nowrap">{t("conv.identity.label")}</span>,
                  memory: <span className="font-mono">{memoryEntry.name}</span>,
                })
              : t("dialog.descriptionNoRunner")}
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={waiting} className="flex min-w-0 flex-col gap-5">
          <div className="grid gap-2 rounded-lg border bg-muted/30 p-3">
            <label className="flex items-center justify-between gap-3 text-sm font-medium">
              {t("dialog.payInWp")}
              <Switch checked={payInWp} onCheckedChange={setPayInWp} />
            </label>
            {payInWp ? (
              <>
                <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
                  <div className="grid gap-1">
                    <Label htmlFor="nc-cap-wp" className="text-xs font-normal text-muted-foreground">
                      {t("budget.cap")}
                    </Label>
                    <Input
                      id="nc-cap-wp"
                      type="number"
                      min={0.01}
                      step="0.01"
                      className="w-32 tabular-nums"
                      value={budgetValue}
                      onChange={(e) => setBudget(Math.max(0, round2(Number(e.target.value) || 0)))}
                    />
                  </div>
                  <div className="pb-1.5 text-[11px] leading-snug text-muted-foreground">
                    <div>{t("buy.balance", { n: balanceOf("wpCredit") })}</div>
                    {budget === null && <div>{t("budget.prefill")}</div>}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t("budget.on")}</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] font-medium tracking-widest text-muted-foreground uppercase">
                    {t("dialog.totalLabel")}
                  </span>
                  <span className="text-xl font-semibold tabular-nums">{t("common.wp", { n: totalWp })}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("budget.off")} {t("budget.switchHint")}
                </p>
              </>
            )}
          </div>
          {runnerEntry ? (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="grid min-w-0 gap-2">
                <Label htmlFor="nc-runner">
                  {t("dialog.runner")}{" "}
                  <span className="text-xs font-normal text-muted-foreground">{t("common.required")}</span>
                </Label>
                <Select value={runnerName} onValueChange={setRunnerName}>
                  <SelectTrigger id="nc-runner" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {runners.map((e) => (
                      <SelectItem key={e.name} value={e.name}>
                        <ServiceIcon entry={e} />
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {capCell("nc-cap-runner", runnerEntry.token)}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>
                {t("dialog.runner")}{" "}
                <span className="text-xs font-normal text-muted-foreground">{t("common.required")}</span>
              </Label>
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangleIcon className="size-4 shrink-0" />
                <span>
                  {rich("dialog.noRunner", {
                    link: (
                      <a className="font-medium underline" href="#/services">
                        {t("dialog.noRunnerLink")}
                      </a>
                    ),
                  })}
                </span>
              </div>
            </div>
          )}
          {models.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="grid min-w-0 gap-2">
                <Label htmlFor="nc-model">{t("dialog.model")}</Label>
                <Select value={modelId} onValueChange={setModelId}>
                  <SelectTrigger id="nc-model" className="w-full min-w-0">
                    <SelectValue placeholder={t("dialog.chooseModel")} />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        <ServiceIcon entry={m.entry} />
                        {m.display}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {model && capCell("nc-cap-model", model.token)}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>{t("dialog.model")}</Label>
              <p className="text-sm text-muted-foreground">
                {rich("dialog.noModel", {
                  link: (
                    <a className="underline" href="#/services">
                      {t("dialog.noModelLink")}
                    </a>
                  ),
                })}
              </p>
            </div>
          )}
          {memoryEntry ? (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="grid min-w-0 gap-2">
                <Label htmlFor="nc-memory">
                  {t("dialog.memory")}{" "}
                  <span className="text-xs font-normal text-muted-foreground">{t("common.required")}</span>
                </Label>
                <Select value={memoryName} onValueChange={setMemoryName}>
                  <SelectTrigger id="nc-memory" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {memories.map((e) => (
                      <SelectItem key={e.name} value={e.name}>
                        <ServiceIcon entry={e} />
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {capCell("nc-cap-memory", memoryEntry.token)}
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>
                {t("dialog.memory")}{" "}
                <span className="text-xs font-normal text-muted-foreground">{t("common.required")}</span>
              </Label>
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                <AlertTriangleIcon className="size-4 shrink-0" />
                <span>
                  {rich("dialog.noMemory", {
                    link: (
                      <a className="font-medium underline" href="#/services">
                        {t("dialog.noRunnerLink")}
                      </a>
                    ),
                  })}
                </span>
              </div>
            </div>
          )}
          <div className="grid gap-2">
            <Label>{t("dialog.tools")}</Label>
            {tools.length > 0 ? (
              <div className="flex flex-col gap-2">
                {tools.map((e) => {
                  const on = selectedTools.includes(e.name);
                  const owner = owners.get(e.token);
                  return (
                    <div key={e.name} className="flex flex-col gap-1.5">
                      <label className="flex items-center gap-2.5 text-sm">
                        <Checkbox
                          checked={on}
                          onCheckedChange={(v) =>
                            setSelectedTools((s) => (v ? [...s, e.name] : s.filter((x) => x !== e.name)))
                          }
                        />
                        <ServiceIcon entry={e} />
                        <span className="font-mono">{e.tool}</span>
                        <span className="truncate text-xs text-muted-foreground">{e.name}</span>
                      </label>
                      {on && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6.5">
                          {capCell(`nc-cap-${e.tool}`, e.token, "w-full")}
                          {owner && owner !== e.tool && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              {t("dialog.sameCap", { owner, token: e.token })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("dialog.noTools")}</p>
            )}
          </div>
        </fieldset>
        <DialogFooter className="sticky -bottom-4 z-10 flex-col bg-muted sm:items-center sm:justify-end">
          {tokensNeeded.length > 0 && (
            <div aria-live="polite" className="min-w-0 text-xs text-muted-foreground sm:mr-auto">
              {Object.keys(allCaps).length === 1
                ? t("dialog.auths1")
                : t("dialog.authsN", { n: Object.keys(allCaps).length })}{" "}
              ·{" "}
              {tt(
                approveNeeded.length > 0
                  ? Y("sigs.approvePlus", { n: approveNeeded.length, symbols: approveNeeded.join(", ") })
                  : Y("sigs.oneSig"),
              )}
            </div>
          )}
          <div className="flex flex-col gap-2 sm:items-end">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={waiting}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void create()} disabled={!valid || waiting}>
                {waiting && <Loader2Icon className="animate-spin" />}
                {t("common.create")}
              </Button>
            </div>
            {waiting && <Waiting n={approveNeeded.length + 1} className="text-xs" />}
            {state === "rejected" && <Rejected className="text-xs" />}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Buy tokens with wpCredit (path A, D55): approve once per token, then exchange. */
export function BuyDialog({ token, onClose }: { token: string; onClose: () => void }) {
  const { directory, balances, tokens, chain, wallet, config, refreshBalances } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const n = useN();
  const meta = tokens.get(token);
  const entry = directory.find((e) => e.token === token);
  const owner = entry ? entry.name.split("/")[0]! : "—";
  const rate = meta?.rate ?? 1_000_000n; // base units per token
  const wpPer = Number(rate) / 1e6;
  const [count, setCount] = useState(10);
  const [creditsText, setCreditsText] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "waiting" | "rejected">("idle");
  const [approved, setApproved] = useState<boolean | null>(null);
  const credits = round2(count * wpPer);
  const wpBalance = balances.wpCredit ?? 0;
  const waiting = state === "waiting";
  const valid = Number.isInteger(count) && count >= 1 && credits <= wpBalance;
  useMemoAsync(async () => {
    if (!wallet || !meta) return;
    const a = await chain.allowance(config.wpCredit, wallet.address, meta.address);
    setApproved(a > 0n);
  }, [wallet, meta]);
  const needsApprove = approved === false || approved === null;
  function buy() {
    if (!valid || waiting || !meta) return;
    setState("waiting");
    request({
      title: Y("wallet.req.buy", { tokens: count, token, credits }),
      amount: credits,
      tokens: [{ amount: count, symbol: token }],
      lines: [
        ...(needsApprove
          ? [lines.approve("wpCredit", meta.address, Y("wallet.plain.approveExchange", { token }))]
          : []),
        lines.exchange(token, credits, owner),
      ],
      onDone: async (r) => {
        if (r !== "signed") return setState("rejected");
        await refreshBalances();
        toast.success(t("buy.toast", { tokens: count, token, credits }));
        onClose();
      },
    });
  }
  return (
    <Dialog open onOpenChange={(v) => !v && !waiting && onClose()}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => waiting && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("buy.title", { token })}</DialogTitle>
          <DialogDescription>{t("buy.description", { token })}</DialogDescription>
        </DialogHeader>
        <fieldset disabled={waiting} className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="buy-tokens">{t("buy.tokens", { token })}</Label>
              <Input
                id="buy-tokens"
                type="number"
                min={1}
                step={1}
                value={count}
                onChange={(e) => {
                  setCreditsText(null);
                  setCount(Math.max(0, Math.floor(Number(e.target.value) || 0)));
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="buy-credits">{t("buy.credits")}</Label>
              <Input
                id="buy-credits"
                type="number"
                min={0}
                step="0.01"
                value={creditsText ?? credits}
                onChange={(e) => {
                  setCreditsText(e.target.value);
                  setCount(Math.max(0, Math.floor((Number(e.target.value) || 0) / wpPer + 1e-9)));
                }}
                onBlur={() => setCreditsText(null)}
              />
            </div>
          </div>
          <div className="grid gap-1 text-xs text-muted-foreground">
            <span className="font-mono">{t("rate.line", { n: round2(1 / wpPer), token })}</span>
            <span>{t(entry?.mine ? "buy.ownerSelf" : "buy.owner", { owner })}</span>
            <span>
              {t("buy.recipient", {
                address: wallet ? `${wallet.address.slice(0, 6)}…${wallet.address.slice(-4)}` : "",
              })}
            </span>
            <span className={credits > wpBalance ? "text-destructive" : undefined}>
              {credits > wpBalance ? t("buy.notEnough") : t("buy.balance", { n: wpBalance })}
            </span>
          </div>
        </fieldset>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-end">
          <span className="text-xs text-muted-foreground">
            {needsApprove ? t("buy.signatures2", { token }) : t("buy.signatures1")}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="outline" onClick={onClose} disabled={waiting}>
              {t("common.cancel")}
            </Button>
            <Button onClick={buy} disabled={!valid || waiting}>
              {waiting && <Loader2Icon className="animate-spin" />}
              {t("rate.buy")}
            </Button>
          </div>
          {waiting && <Waiting n={needsApprove ? 2 : 1} className="text-xs" />}
          {state === "rejected" && <Rejected className="text-xs" />}
          <span className="sr-only">{n(count)}</span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

import { useEffect } from "react";
function useMemoAsync(fn: () => Promise<void>, deps: unknown[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void fn(), deps);
}
